import { newId, orderTodo, overdue, todayBounds, type Notebook, type Tag, type TodoFilter, type TodoTask } from "@engram/core";
import { BookPlus, CircleCheckBig, CirclePlus, FilePlus2, Flag, MoreHorizontal, Pencil, Pin, Plus, X } from "lucide-react";
import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { Link, useLocation } from "wouter";
import { createNote, createNotebook, createTask, notebookNameError, setSetting, toggleTask, trashNotes } from "../db/actions";
import { useQuery, useRow } from "../db/client";
import { useThumbnailUrl } from "./attachments";
import { dateFmt, fullDate, relativeDate, shortDate } from "./format";
import { useLayout } from "./layout";
import { Menu, Prompt, useAnchor, type MenuEntry } from "./Menu";
import { openCounts, suggestedNoteIds } from "./opens";
import { usePrefs } from "./prefs";
import type { NoteRow } from "./queries";
import { shortcutHref } from "./Shell";
import { ShortcutIcon, ShortcutsDialog, useShortcuts } from "./Shortcuts";
import { DefaultTaskNoteDialog } from "./TasksPage";
import { firstName, useProfile } from "./profile";

/*
 * Evernote web's Home: a "Home" header with a pencil to customize, and widgets on a three-column grid. A widget is
 * small (one column), medium (two) or large (the whole row). Customizing shows Evernote's banner (Cancel / Done),
 * a remove button and size picker on each widget, drag to reorder, and the widgets that can be added back.
 * On a phone it's Evernote Android's: a greeting, then the widgets in one column.
 */

type WidgetId = "notes" | "scratch" | "notebooks" | "shortcuts" | "tasks" | "pinned" | "tags";
type Size = "S" | "M" | "L";
const ALL: WidgetId[] = ["notes", "scratch", "notebooks", "shortcuts", "tasks", "pinned", "tags"];
const LABEL: Record<WidgetId, string> = {
  notes: "Notes",
  scratch: "Scratch Pad",
  notebooks: "Notebooks",
  shortcuts: "Shortcuts",
  tasks: "To-do",
  pinned: "Pinned Note",
  tags: "Tags",
};

interface Layout {
  order: WidgetId[];
  hidden: WidgetId[];
  sizes?: Partial<Record<WidgetId, Size>>;
}
// The owner's Evernote Home: Notes across the top, then Scratch Pad, Notebooks and Shortcuts; To-do beside the
// Scratch Pad (owner, 12 Oct: "it should be next to scratch pad").
const DEFAULT: Layout = { order: ["notes", "scratch", "tasks", "notebooks", "shortcuts", "pinned", "tags"], hidden: ["pinned", "tags"], sizes: { notes: "L" } };
const sizeOf = (l: Layout, w: WidgetId): Size => l.sizes?.[w] ?? (w === "notes" ? "L" : "S");

const COLUMNS = { wide: 3, compact: 2, narrow: 1 } as const;
const SPAN: Record<Size, number> = { S: 1, M: 2, L: 3 };

/**
 * How many columns each widget takes: its size, except that the last widget of a row with free columns stretches
 * across them, so no row ends in a gap (desktop: Scratch Pad | To-do | Notebooks, then Shortcuts across; the Fold's
 * inner screen: Scratch Pad | To-do, then Notebooks | Shortcuts). Not while customizing: the sizes show as picked.
 */
export function widgetSpans(sizes: Size[], cols: number, fill: boolean): number[] {
  const out: number[] = [];
  let used = 0;
  for (const size of sizes) {
    const s = Math.min(SPAN[size], cols);
    if (used + s > cols) {
      if (fill) out[out.length - 1]! += cols - used;
      used = 0;
    }
    out.push(s);
    used = (used + s) % cols;
  }
  if (fill && used > 0) out[out.length - 1]! += cols - used;
  return out;
}

function normalize(saved: Layout | null): Layout {
  const l = saved ?? DEFAULT;
  return { order: [...l.order.filter((w) => ALL.includes(w)), ...ALL.filter((w) => !l.order.includes(w))], hidden: l.hidden.filter((w) => ALL.includes(w)), sizes: l.sizes ?? {} };
}

function greeting(d = new Date()) {
  const h = d.getHours();
  return h < 5 ? "Good evening" : h < 12 ? "Good morning" : h < 18 ? "Good afternoon" : "Good evening";
}

export function HomePage() {
  const layoutRow = useRow<{ value: string }>("SELECT value FROM settings WHERE id = 'home.layout' AND deleted = 0", [], ["settings"]);
  // Evernote's "Good evening, Peter!": the first name.
  const name = firstName(useProfile().name);
  // Evernote Android's Home on both Fold screens: the cover (narrow) and the inner screen (compact).
  const mode = useLayout();
  const phone = mode !== "wide";
  const saved = normalize(layoutRow ? (JSON.parse(layoutRow.value) as Layout) : null);
  const [draft, setDraft] = useState<Layout | null>(null); // while customizing
  const layout = draft ?? saved;
  const [dragging, setDragging] = useState<WidgetId | null>(null);

  const visible = layout.order.filter((w) => !layout.hidden.includes(w));
  const hidden = layout.order.filter((w) => layout.hidden.includes(w));
  const spans = widgetSpans(visible.map((w) => sizeOf(layout, w)), COLUMNS[mode], !draft);
  // Edits build on the latest draft (several can land before a re-render, e.g. while dragging).
  const latest = useRef<Layout | null>(null);
  latest.current = draft;
  const change = (next: (l: Layout) => Partial<Layout>) => {
    const base = latest.current ?? saved;
    const l = { ...base, ...next(base) };
    if (latest.current) {
      latest.current = l;
      setDraft(l);
    } else void setSetting("home.layout", l);
  };
  const remove = (w: WidgetId) => change((l) => ({ hidden: [...l.hidden, w] }));
  const add = (w: WidgetId) => change((l) => ({ hidden: l.hidden.filter((x) => x !== w), order: [...l.order.filter((x) => x !== w), w] }));
  const moveBefore = (w: WidgetId, target: WidgetId) => {
    if (w === target) return;
    change((l) => {
      const order = l.order.filter((x) => x !== w);
      order.splice(order.indexOf(target), 0, w);
      return { order };
    });
  };
  const resize = (w: WidgetId, size: Size) => change((l) => ({ sizes: { ...l.sizes, [w]: size } }));

  return (
    <div className={`home${draft ? " customizing" : ""}`}>
      <div className="home-main">
        {draft ? (
          <div className="customize-banner">
            <strong>Customize your Home</strong>
            <span>Drag widgets where you want them.</span>
            <div className="grow" />
            <button className="btn" onClick={() => ((latest.current = null), setDraft(null))}>Cancel</button>
            <button
              className="btn primary"
              onClick={() => {
                if (latest.current) void setSetting("home.layout", latest.current);
                latest.current = null;
                setDraft(null);
              }}
            >
              Done
            </button>
          </div>
        ) : phone ? (
          <header className="home-header greeting">
            <div>
              <h1>{greeting()}{name ? `, ${name}` : ""}!</h1>
              <div className="home-date">{new Date().toLocaleDateString(undefined, { weekday: "long", year: "numeric", month: "long", day: "numeric" }).toUpperCase()}</div>
            </div>
          </header>
        ) : (
          <header className="home-header">
            <h1>Home</h1>
            <button className="icon-btn round" title="Customize Home" aria-label="Customize Home" onClick={() => ((latest.current = saved), setDraft(saved))}>
              <Pencil size={16} />
            </button>
          </header>
        )}
        <div className="home-grid">
          {visible.map((w, i) => (
            <section
              key={w}
              className={`widget size-${sizeOf(layout, w)}${dragging === w ? " dragging" : ""}`}
              style={{ gridColumn: `span ${spans[i]}` }}
              draggable={!!draft}
              onDragStart={(e) => {
                setDragging(w);
                e.dataTransfer.effectAllowed = "move";
                e.dataTransfer.setData("text/plain", w);
              }}
              onDragOver={(e) => {
                if (!dragging) return;
                e.preventDefault();
                if (dragging !== w) moveBefore(dragging, w);
              }}
              onDragEnd={() => setDragging(null)}
              onDrop={(e) => e.preventDefault()}
            >
              <Widget id={w} span={spans[i]!} phone={phone} onRemove={() => remove(w)} />
              {draft && (
                <>
                  <button className="widget-remove" aria-label={`Remove ${LABEL[w]}`} onClick={() => remove(w)}>
                    <X size={13} />
                  </button>
                  <div className="widget-size">
                    <span>Widget Size</span>
                    {(["S", "M", "L"] as Size[]).map((s) => (
                      <button key={s} className={`size-opt size-opt-${s}${sizeOf(layout, w) === s ? " on" : ""}`} aria-label={{ S: "Small", M: "Medium", L: "Large" }[s]} onClick={() => resize(w, s)} />
                    ))}
                  </div>
                </>
              )}
            </section>
          ))}
          {draft && visible.length === 0 && <div className="widget-drop">Add widgets from the list</div>}
        </div>
        {phone && !draft && (
          <button className="btn customize-phone" onClick={() => ((latest.current = saved), setDraft(saved))}>
            Customize your Widgets
          </button>
        )}
      </div>
      {draft && (
        <aside className="widget-panel">
          <div className="widget-panel-head">
            <strong>Available widgets</strong>
            <div className="grow" />
            <button className="link-btn" onClick={() => ((latest.current = normalize(DEFAULT)), setDraft(latest.current))}>Reset</button>
          </div>
          {hidden.map((w) => (
            <button key={w} className="widget-option" onClick={() => add(w)}>
              <Plus size={14} />
              <span>{LABEL[w]}</span>
            </button>
          ))}
          {hidden.length === 0 && <p className="muted small">Every widget is on your Home.</p>}
        </aside>
      )}
    </div>
  );
}

function Widget({ id, span, phone, onRemove }: { id: WidgetId; span: number; phone: boolean; onRemove: () => void }) {
  switch (id) {
    case "notes":
      return <NotesWidget phone={phone} onRemove={onRemove} />;
    case "scratch":
      return <ScratchPad onRemove={onRemove} />;
    case "shortcuts":
      return <ShortcutsWidget span={span} phone={phone} onRemove={onRemove} />;
    case "pinned":
      return <PinnedWidget onRemove={onRemove} />;
    case "tasks":
      return <TodoWidget onRemove={onRemove} />;
    case "notebooks":
      return <NotebooksWidget onRemove={onRemove} />;
    case "tags":
      return <TagsWidget onRemove={onRemove} />;
  }
}

/** Widget title with Evernote's hover actions: a create button and a ··· menu ending in Remove widget. */
function WidgetHead({ title, href, actions, entries, onRemove }: { title: string; href?: string; actions?: ReactNode; entries: MenuEntry[]; onRemove: () => void }) {
  const m = useAnchor();
  return (
    <div className={`widget-head${m.anchor ? " menu-open" : ""}`}>
      {href ? (
        <Link href={href} className="widget-title">{title}</Link>
      ) : (
        <span className="widget-title">{title}</span>
      )}
      <div className="grow" />
      <div className="widget-actions">
        {actions}
        <button className="icon-btn" onClick={m.open} aria-label={`${title} options`} title="More actions">
          <MoreHorizontal size={16} />
        </button>
      </div>
      {m.anchor && (
        <Menu anchor={m.anchor} align="end" onClose={m.close} entries={[...entries, ...(entries.length ? [{ separator: true } as const] : []), { label: "Remove widget", onSelect: onRemove }]} />
      )}
    </div>
  );
}

function Tabs<T extends string>({ value, options, onChange }: { value: T; options: [T, string][]; onChange: (v: T) => void }) {
  return (
    <div className="widget-tabs">
      {options.map(([k, l]) => (
        <button key={k} className={`tab${value === k ? " active" : ""}`} onClick={() => onChange(k)}>{l}</button>
      ))}
    </div>
  );
}

const NOTE_COLS = `n.id, n.title, substr(n.body_text, 1, 200) AS snippet, n.updated, n.created, n.notebook_id, n.tag_ids, n.pinned_at, n.trashed_at,
  (SELECT name FROM notebooks WHERE id = n.notebook_id) AS notebook,
  (SELECT a.id FROM attachments a WHERE a.note_id = n.id AND a.deleted = 0 AND a.mime LIKE 'image/%' ORDER BY a.created LIMIT 1) AS thumb`;

type HomeNote = NoteRow & { notebook: string | null };

function NotesWidget({ phone, onRemove }: { phone: boolean; onRemove: () => void }) {
  const [tab, setTab] = useState<"recent" | "suggested">("recent");
  const suggested = useMemo(() => suggestedNoteIds(), []);
  const recent = useQuery<HomeNote>(`SELECT ${NOTE_COLS} FROM notes n WHERE n.deleted = 0 AND n.trashed_at IS NULL ORDER BY n.updated DESC LIMIT 16`, [], ["notes", "attachments"]);
  const sug = useQuery<HomeNote>(
    tab === "suggested" && suggested.length ? `SELECT ${NOTE_COLS} FROM notes n WHERE n.deleted = 0 AND n.trashed_at IS NULL AND n.id IN (${suggested.map(() => "?").join(",")})` : null,
    suggested,
    ["notes", "attachments"],
  );
  const list = tab === "recent" ? recent : sug?.sort((a, b) => suggested.indexOf(a.id) - suggested.indexOf(b.id));
  const [, navigate] = useLocation();
  const newNote = async () => navigate(`/note/${await createNote()}`);
  return (
    <>
      <WidgetHead
        title="Notes"
        href="/notes"
        onRemove={onRemove}
        actions={<button className="icon-btn" title="Create new note" aria-label="Create new note" onClick={() => void newNote()}><FilePlus2 size={16} className="ic-create" /></button>}
        entries={[
          { label: "Go to Notes", onSelect: () => navigate("/notes") },
          { label: "Create new note", onSelect: () => void newNote() },
        ]}
      />
      {phone && <Tabs value={tab} onChange={setTab} options={[["recent", "Recent"], ["suggested", "Suggested"]]} />}
      <div className="card-strip">
        {list?.map((n) => <HomeNoteCard key={n.id} note={n} onOpen={() => navigate(`/note/${n.id}`)} />)}
        {tab === "suggested" && !list?.length && <p className="muted small">Notes you open often will show up here.</p>}
        {list?.length === 0 && tab === "recent" && (
          <button className="home-card add" onClick={() => void newNote()}>
            <FilePlus2 size={20} className="ic-create" />
            <span>Create new note</span>
          </button>
        )}
      </div>
    </>
  );
}

function HomeNoteCard({ note, onOpen }: { note: HomeNote; onOpen: () => void }) {
  const thumb = useThumbnailUrl(note.thumb);
  return (
    <button className={`home-card${thumb ? " with-thumb" : ""}`} onClick={onOpen}>
      {note.notebook && <span className="home-card-nb">{note.notebook}</span>}
      <span className="home-card-title">{note.title || "Untitled"}</span>
      {note.snippet && <span className="home-card-snippet">{note.snippet}</span>}
      {thumb && <img src={thumb} alt="" className="home-card-thumb" />}
      <span className="grow" />
      <span className="home-card-date">{relativeDate(note.updated)}</span>
    </button>
  );
}

function ScratchPad({ onRemove }: { onRemove: () => void }) {
  const row = useRow<{ value: string; updated: number }>("SELECT value, updated FROM settings WHERE id = 'scratchPad' AND deleted = 0", [], ["settings"]);
  const [text, setText] = useState("");
  const editing = useRef(false);
  const timer = useRef<ReturnType<typeof setTimeout>>(undefined);
  const [, navigate] = useLocation();
  useEffect(() => {
    if (row !== undefined && !editing.current) setText(row ? (JSON.parse(row.value) as string) : "");
  }, [row]);
  const unsaved = useRef<string | null>(null);
  const flush = () => {
    clearTimeout(timer.current);
    editing.current = false;
    const v = unsaved.current;
    unsaved.current = null;
    if (v !== null) void setSetting("scratchPad", v);
  };
  const save = (v: string) => {
    unsaved.current = v;
    clearTimeout(timer.current);
    timer.current = setTimeout(flush, 600);
  };
  // Leaving Home or the page right after typing keeps the text.
  useEffect(() => {
    window.addEventListener("pagehide", flush);
    return () => {
      window.removeEventListener("pagehide", flush);
      flush();
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const toNote = async (trash: boolean) => {
    const v = text.trim();
    if (!v) return;
    // A save still waiting would put the text back after it was converted.
    clearTimeout(timer.current);
    unsaved.current = null;
    editing.current = false;
    // Evernote: the first line becomes the title, the whole text the body.
    const title = v.split("\n")[0]!.slice(0, 80);
    const html = v.split("\n").map((l) => `<p>${l.replace(/[&<>]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;" })[c]!)}</p>`).join("");
    const id = await createNote({ title, bodyHtml: html, bodyText: v });
    if (trash) await trashNotes([id]);
    setText("");
    await setSetting("scratchPad", "");
    if (!trash) navigate(`/note/${id}`);
  };
  return (
    <>
      <WidgetHead
        title="Scratch Pad"
        onRemove={onRemove}
        entries={[
          { label: "Convert to note", disabled: !text.trim(), onSelect: () => void toNote(false) },
          { label: "Clear scratch pad", disabled: !text.trim(), hint: "Moves it to Trash", onSelect: () => void toNote(true) },
        ]}
      />
      <textarea
        className="scratch"
        placeholder="Start writing…"
        value={text}
        onChange={(e) => {
          editing.current = true;
          setText(e.target.value);
          save(e.target.value);
        }}
      />
    </>
  );
}

function ShortcutsWidget({ span, phone, onRemove }: { span: number; phone: boolean; onRemove: () => void }) {
  const rows = useShortcuts();
  const [editing, setEditing] = useState(false);
  const [, navigate] = useLocation();
  return (
    <>
      <WidgetHead
        title="Shortcuts"
        href="/shortcuts"
        onRemove={onRemove}
        entries={[
          { label: "Go to Shortcuts", onSelect: () => navigate("/shortcuts") },
          ...(rows?.length ? [{ label: "Reorder shortcuts…", onSelect: () => setEditing(true) }] : []),
        ]}
      />
      <div className={phone ? "shortcut-rows cols-2 tiles" : `shortcut-rows cols-${span}`}>
        {rows?.map((s) => (
          <Link key={s.id} href={shortcutHref(s.kind, s.target_id)} className="shortcut-row">
            {phone ? (
              <span className="tile-icon">
                <ShortcutIcon kind={s.kind} size={18} />
              </span>
            ) : (
              <ShortcutIcon kind={s.kind} size={15} />
            )}
            <span>{s.label || "Untitled"}</span>
          </Link>
        ))}
        {rows?.length === 0 && <p className="muted small">Add shortcuts from a note's or notebook's ··· menu.</p>}
      </div>
      {editing && <ShortcutsDialog onClose={() => setEditing(false)} />}
    </>
  );
}

function PinnedWidget({ onRemove }: { onRemove: () => void }) {
  const pinned = useRow<{ value: string }>("SELECT value FROM settings WHERE id = 'home.pinnedNote' AND deleted = 0", [], ["settings"]);
  const id = pinned ? (JSON.parse(pinned.value) as string | null) : null;
  const note = useRow<{ id: string; title: string; body_text: string; updated: number }>(id ? "SELECT id, title, body_text, updated FROM notes WHERE id = ? AND deleted = 0 AND trashed_at IS NULL" : null, [id], ["notes"]);
  const [, navigate] = useLocation();
  return (
    <>
      <WidgetHead
        title="Pinned Note"
        onRemove={onRemove}
        entries={note ? [{ label: "Open note", onSelect: () => navigate(`/note/${note.id}`) }, { label: "Unpin", onSelect: () => void setSetting("home.pinnedNote", null) }] : []}
      />
      {note ? (
        <button className="pinned-note" onClick={() => navigate(`/note/${note.id}`)}>
          <span className="home-card-title">{note.title || "Untitled"}</span>
          <span className="pinned-body">{note.body_text.slice(0, 600)}</span>
          <span className="home-card-date">{relativeDate(note.updated)}</span>
        </button>
      ) : (
        <p className="muted small"><Pin size={13} /> Pin a note here with "Pin to Home" in the note's ··· menu.</p>
      )}
    </>
  );
}

type TodoRow = { id: string; title: string; due_at: number | null; due_all_day: number; flagged: number; note_id: string };
type HomeTodo = TodoTask & { noteId: string };

/** Tasks added from this panel during this visit to the app: listed first, as the Android widget lists its own adds. */
const justAdded = new Set<string>();

/** Re-renders every minute, so "overdue" and today's bounds stay right while Home stays open. */
function useMinute() {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 60_000);
    return () => clearInterval(t);
  }, []);
  return now;
}

/**
 * To-do (owner, 12 Oct: "a todo list … on my home page of both apps that gives same view as the widget"): the Android
 * To-do widget's view and order (core `orderTodo`, shared with TodoRules.kt): just added first, then due today, overdue
 * (latest first), flagged, the rest; All | Today | Flagged; tick (repeats advance); quick add; a row opens its note.
 * Its list is as tall as the Notebooks list, so the two (and the Scratch Pad) line up.
 */
function TodoWidget({ onRemove }: { onRemove: () => void }) {
  const filter = usePrefs((p) => p.homeTodo);
  const setPrefs = usePrefs((p) => p.set);
  const rows = useQuery<TodoRow>(
    `SELECT t.id, t.title, t.due_at, t.due_all_day, t.flagged, t.note_id FROM tasks t JOIN notes n ON n.id = t.note_id
     WHERE t.deleted = 0 AND t.completed_at IS NULL AND n.deleted = 0 AND n.trashed_at IS NULL
     ORDER BY (t.due_at IS NULL), t.due_at, t.created LIMIT 300`,
    [],
    ["tasks", "notes"],
  );
  const now = useMinute();
  const { start, end } = todayBounds(now);
  const tasks = useMemo(
    () => orderTodo<HomeTodo>((rows ?? []).map((r) => ({ id: r.id, title: r.title, due: r.due_at, allDay: !!r.due_all_day, flagged: !!r.flagged, noteId: r.note_id, justAdded: justAdded.has(r.id) })), filter, start, end),
    [rows, filter, start, end],
  );
  const [adding, setAdding] = useState(false);
  const [pickNote, setPickNote] = useState(false);
  const [, navigate] = useLocation();
  const empty = filter === "today" ? "Nothing due today" : filter === "flagged" ? "No flagged tasks" : "Nothing to do";
  return (
    <>
      <WidgetHead
        title="To-do"
        href="/tasks"
        onRemove={onRemove}
        actions={<button className="icon-btn" title="Add a task" aria-label="Add a task" onClick={() => setAdding(true)}><CirclePlus size={16} className="ic-task" /></button>}
        entries={[
          { label: "Go to Tasks", onSelect: () => navigate("/tasks") },
          { label: "Add a task", onSelect: () => setAdding(true) },
          { label: "Default task note…", hint: "Where tasks added here go", onSelect: () => setPickNote(true) },
        ]}
      />
      <Tabs<TodoFilter> value={filter} onChange={(v) => setPrefs({ homeTodo: v })} options={[["all", "All"], ["today", "Today"], ["flagged", "Flagged"]]} />
      <div className="home-todo" role="list" aria-label="To-do">
        {adding && <TodoAdd key={filter} filter={filter} onClose={() => setAdding(false)} />}
        {tasks.map((t) => (
          <TodoItem key={t.id} task={t} now={now} start={start} end={end} onOpen={() => navigate(`/note/${t.noteId}`)} />
        ))}
        {rows && tasks.length === 0 && !adding && (
          <div className="home-todo-empty">
            <CircleCheckBig size={22} className="home-todo-empty-icon" aria-hidden="true" />
            <span className="home-todo-empty-title">{empty}</span>
            <button className="home-todo-empty-add" onClick={() => setAdding(true)}>
              <CirclePlus size={15} className="ic-task" /> Add a task
            </button>
          </div>
        )}
      </div>
      {pickNote && <DefaultTaskNoteDialog onClose={() => setPickNote(false)} />}
    </>
  );
}

/** The widget's due label: "Today", a time today, "Yesterday", else the date; red once overdue, plum when due today. */
function dueLabel(t: TodoTask, start: number, end: number): string {
  if (t.due == null) return "";
  if (t.due >= start && t.due <= end) return t.allDay ? "Today" : dateFmt({ hour: "numeric", minute: "2-digit" }).format(t.due);
  if (t.due >= start - 86_400_000 && t.due < start) return "Yesterday";
  return shortDate(t.due);
}

function TodoItem({ task: t, now, start, end, onOpen }: { task: HomeTodo; now: number; start: number; end: number; onOpen: () => void }) {
  const [ticked, setTicked] = useState(false);
  const label = dueLabel(t, start, end);
  const tone = overdue(t, now, start) ? " overdue" : t.due != null && t.due <= end ? " today" : "";
  return (
    <div role="listitem" className={`home-todo-row${ticked ? " ticked" : ""}`}>
      <button
        className="home-todo-check"
        role="checkbox"
        aria-checked={ticked}
        aria-label={`Complete ${t.title || "Untitled task"}`}
        onClick={() => {
          if (ticked) return;
          setTicked(true);
          // A moment to see the tick; a repeating task then comes back with its next date.
          setTimeout(() => void toggleTask(t.id).finally(() => setTicked(false)), 350);
        }}
      >
        <span className={`task-check${ticked ? " checked" : ""}`} />
      </button>
      <button className="home-todo-open" onClick={onOpen} title={t.title || "Untitled task"}>
        <span className="home-todo-title">{t.title || "Untitled task"}</span>
        {t.flagged && <Flag size={13} className="home-todo-flag" aria-label="Flagged" />}
        {label && <span className={`home-todo-due${tone}`}>{label}</span>}
      </button>
    </div>
  );
}

/**
 * Quick add, like the widget's "+": a title, and "Today" (on when the Today filter is showing) and a flag (on under
 * Flagged, so the new task stays in view). Enter adds and stays open for the next; leaving with text adds it too.
 */
function TodoAdd({ filter, onClose }: { filter: TodoFilter; onClose: () => void }) {
  const [title, setTitle] = useState("");
  const [today, setToday] = useState(filter === "today");
  const [flag, setFlag] = useState(filter === "flagged");
  const add = () => {
    const v = title.trim();
    if (!v) return;
    setTitle("");
    const id = newId();
    justAdded.add(id);
    void createTask({ id, title: v, flagged: flag, ...(today ? { due_at: todayBounds().start, due_all_day: 1 } : {}) });
  };
  const keep = (e: React.MouseEvent) => e.preventDefault(); // the toggles don't take the focus from the title
  return (
    <form
      className="home-todo-add"
      onSubmit={(e) => {
        e.preventDefault();
        add();
      }}
    >
      <span className="task-check" aria-hidden="true" />
      <input
        autoFocus
        className="home-todo-input"
        placeholder="Add a task"
        aria-label="New task"
        maxLength={500}
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Escape") {
            e.preventDefault();
            onClose();
          }
        }}
        onBlur={() => {
          add();
          onClose();
        }}
      />
      <button type="button" className={`home-todo-toggle${today ? " on" : ""}`} aria-pressed={today} onMouseDown={keep} onClick={() => setToday((v) => !v)}>
        Today
      </button>
      <button type="button" className={`home-todo-toggle icon${flag ? " on" : ""}`} aria-pressed={flag} aria-label="Flag" title="Flag" onMouseDown={keep} onClick={() => setFlag((v) => !v)}>
        <Flag size={13} />
      </button>
    </form>
  );
}

type HomeNotebook = Notebook & { n: number; last: number | null; edits: number; opens: number };
const USE_DAYS = 30;

/**
 * Notebooks as a list (not a side-scrolling strip, owner 2026-10-10): Recent = the last edited first; Most used = how
 * often its notes were opened on this device and edited anywhere in the last 30 days. One panel with ruled rows and a
 * notebook-spine mark, so it doesn't read like the Shortcuts tiles next to it. One line per notebook (owner, 11 Oct:
 * "need a better way to show more notebooks"): six show at once beside the Scratch Pad, up to 12 by scrolling.
 */
function NotebooksWidget({ onRemove }: { onRemove: () => void }) {
  const tab = usePrefs((p) => p.homeNotebooks);
  const setPrefs = usePrefs((p) => p.set);
  // Read once per visit to Home: opens change only by opening notes, which leaves this page.
  const opens = useMemo(() => JSON.stringify(openCounts(USE_DAYS)), []);
  const since = useMemo(() => Date.now() - USE_DAYS * 86400000, []);
  const all = useQuery<HomeNotebook>(
    `SELECT nb.*, COALESCE(c.n, 0) AS n, c.last AS last, COALESCE(c.edits, 0) AS edits, COALESCE(o.opens, 0) AS opens
     FROM notebooks nb
     LEFT JOIN (SELECT notebook_id, COUNT(*) AS n, MAX(updated) AS last, SUM(updated > ?) AS edits FROM notes WHERE deleted = 0 AND trashed_at IS NULL GROUP BY notebook_id) c ON c.notebook_id = nb.id
     LEFT JOIN (SELECT n.notebook_id, SUM(j.value) AS opens FROM json_each(?) j JOIN notes n ON n.id = j.key WHERE n.deleted = 0 AND n.trashed_at IS NULL GROUP BY n.notebook_id) o ON o.notebook_id = nb.id
     WHERE nb.deleted = 0 AND nb.kind = 'notebook'`,
    [since, opens],
    ["notebooks", "notes"],
  );
  const rows = useMemo(() => {
    const list = [...(all ?? [])];
    if (tab === "recent") list.sort((a, b) => (b.last ?? 0) - (a.last ?? 0) || a.name.localeCompare(b.name));
    else list.sort((a, b) => b.opens + b.edits - (a.opens + a.edits) || b.n - a.n || (b.last ?? 0) - (a.last ?? 0));
    return list.slice(0, 12);
  }, [all, tab]);
  const [, navigate] = useLocation();
  const [creating, setCreating] = useState(false);
  return (
    <>
      <WidgetHead
        title="Notebooks"
        href="/notebooks"
        onRemove={onRemove}
        actions={<button className="icon-btn" title="Create new notebook" aria-label="Create new notebook" onClick={() => setCreating(true)}><BookPlus className="ic-create" size={16} /></button>}
        entries={[
          { label: "Go to Notebooks", onSelect: () => navigate("/notebooks") },
          { label: "Create new notebook", onSelect: () => setCreating(true) },
        ]}
      />
      <Tabs value={tab} onChange={(v) => setPrefs({ homeNotebooks: v })} options={[["recent", "Recent"], ["used", "Most used"]]} />
      <div className="home-nb-list" role="list">
        {rows.map((nb) => (
          <button
            key={nb.id}
            role="listitem"
            className="home-nb-row"
            title={
              tab === "used"
                ? `Last ${USE_DAYS} days: opened ${nb.opens} ${nb.opens === 1 ? "time" : "times"} on this device, ${nb.edits} ${nb.edits === 1 ? "note" : "notes"} edited`
                : `${nb.n} ${nb.n === 1 ? "note" : "notes"}${nb.last ? `, last edited ${fullDate(nb.last)}` : ""}`
            }
            onClick={() => navigate(`/notebook/${nb.id}`)}
          >
            <span className="home-nb-spine" aria-hidden="true" />
            <span className="home-nb-name">{nb.name}</span>
            <span className="home-nb-meta">{tab === "recent" ? (nb.last ? relativeDate(nb.last) : "No notes yet") : nb.opens + nb.edits ? usage(nb.opens, nb.edits) : "Not used lately"}</span>
            <span className="home-nb-count" aria-label={`${nb.n} ${nb.n === 1 ? "note" : "notes"}`}>
              {nb.n}
            </span>
          </button>
        ))}
        {all?.length === 0 && <p className="muted small home-nb-empty">No notebooks yet.</p>}
      </div>
      {creating && (
        <Prompt
          title="Create new notebook"
          label="Name"
          confirm="Create"
          validate={notebookNameError("notebook")}
          onSubmit={async (v) => navigate(`/notebook/${await createNotebook(v)}`)}
          onClose={() => setCreating(false)}
        />
      )}
    </>
  );
}

/** "9 opens · 4 edits" over the last 30 days (the row's tooltip says it in full). */
function usage(opens: number, edits: number): string {
  return [opens ? `${opens} ${opens === 1 ? "open" : "opens"}` : "", edits ? `${edits} ${edits === 1 ? "edit" : "edits"}` : ""].filter(Boolean).join(" · ");
}

function TagsWidget({ onRemove }: { onRemove: () => void }) {
  const tags = useQuery<Tag & { n: number }>(
    `SELECT t.*, COALESCE(c.n, 0) AS n FROM tags t LEFT JOIN (SELECT je.value AS tag_id, COUNT(*) AS n FROM notes n, json_each(n.tag_ids) je WHERE n.deleted = 0 AND n.trashed_at IS NULL GROUP BY je.value) c ON c.tag_id = t.id WHERE t.deleted = 0 ORDER BY t.name COLLATE NOCASE`,
    [],
    ["tags", "notes"],
  );
  return (
    <>
      <WidgetHead title="Tags" onRemove={onRemove} entries={[]} />
      <div className="tag-cloud">
        {tags?.map((t) => (
          <Link key={t.id} href={`/tag/${t.id}`} className="chip link">{t.name} <span className="muted">{t.n}</span></Link>
        ))}
        {tags?.length === 0 && <p className="muted small">No tags yet.</p>}
      </div>
    </>
  );
}
